import type { AdminNav, AdminNavGroup, AdminNavItem, AdminNavSubGroup } from '~kui/types/adminNav';

import { ICONS } from '~ds/lib/icons';

import { Capability } from '~/graphql/generated/graphql';

/**
 * The Users group's label. A user detail page is reachable from either cohort list, so no nav item
 * claims its route and the page has to name the group itself in its breadcrumbs.
 */
export const USERS_NAV_GROUP_LABEL = 'Users';

export const useAdminNav = async () => {
  const { t } = useI18n();
  const { logout } = useAuth();
  const { isAus, isUsa } = useClusterCountryConfig();
  const { capabilities } = useCapabilities();
  const { getFlag } = useFeatureFlags();
  const passwordLoginEnabled = await getFlag('password-login-enabled');
  const cashoutAdminEnabled = await getFlag('cashout-admin-enabled');

  // Widened to strings because AdminNavItem.capability is schema-agnostic.
  const grantedCapabilities = computed(() => new Set<string>(capabilities.value));

  // A capability the viewer lacks marks the item rather than hiding it: the sidebar
  // stays a stable map of the admin surface, and clicking through lands on the
  // capability gate's locked takeover. Region and flag gating still hide outright.
  const buildItems = (items: AdminNavItem[]): AdminNavItem[] =>
    items
      .filter(item => item.isVisible !== false)
      .map(item => ({
        ...item,
        isLocked: !!item.capability && !grantedCapabilities.value.has(item.capability),
      }));

  const nav = computed<AdminNav>(() => {
    const groups: AdminNavGroup[] = [
      {
        label: 'Content',
        subGroups: [
          {
            label: 'Learning Assets',
            items: [
              {
                to: '/content/learning-assets',
                label: t('content.learningAssets.nav.learningAssets'),
                iconPath: ICONS.wrench,
                activeRoutes: [
                  'content-learning-assets-index',
                  'content-story-assets-create',
                  'content-id-index',
                  'content-story-assets-settings',
                  'content-story-assets-id-transcript',
                ],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
              {
                to: '/content/industries',
                label: 'Industries',
                iconPath: ICONS.briefcase,
                activeRoutes: ['content-industry-list-index'],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
              {
                to: '/content/consideration-areas',
                label: 'Consideration Areas',
                iconPath: ICONS.star,
                activeRoutes: ['content-consideration-areas-index'],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
              {
                to: '/content/articles',
                label: 'Articles',
                iconPath: ICONS.edit,
                activeRoutes: ['content-articles-index'],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
              {
                to: '/content/videos',
                label: 'Videos',
                iconPath: ICONS.play,
                activeRoutes: ['content-videos-index'],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
            ],
          },
          {
            label: 'Learning Asset Collections',
            items: [
              {
                to: '/content/channels',
                label: t('content.channels.title'),
                iconPath: ICONS.tvFix,
                activeRoutes: ['content-channels-index'],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
              {
                to: '/content/teacher-templates',
                label: 'Teacher templates',
                iconPath: ICONS.film,
                activeRoutes: ['content-teacher-templates-index'],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
              {
                to: '/content/categories',
                label: 'Collection Categories',
                iconPath: ICONS.folder,
                activeRoutes: ['content-categories'],
                isVisible: true,
                capability: Capability.AdminContentView,
              },
            ],
          },
        ],
        items: [
          {
            to: '/content/content-assets',
            label: 'Content Assets',
            iconPath: ICONS.image,
            activeRoutes: ['content-content-assets-index'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
          {
            to: '/content/faqs',
            label: 'FAQs',
            iconPath: ICONS.messageQuestion,
            activeRoutes: ['content-faqs-index'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
          {
            to: '/content/utm-key-messages',
            label: 'Utm Overrides',
            iconPath: ICONS.link,
            activeRoutes: ['content-utm-key-messages-index'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
          {
            to: '/content/badges',
            label: 'Badges',
            iconPath: ICONS.starAward,
            activeRoutes: ['content-badges-index'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
          {
            to: '/content/feedback-survey',
            label: t('content.feedbackSurvey.title'),
            iconPath: ICONS.messageQuestion,
            activeRoutes: ['content-feedback-survey-index'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
          {
            to: '/content/audit',
            label: 'Content Audit',
            iconPath: ICONS.listSolid,
            activeRoutes: ['content-audit'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
          {
            to: '/content/curriculum-frameworks',
            label: t('content.curriculumFrameworks.title'),
            iconPath: ICONS.graduationCap,
            activeRoutes: ['content-curriculum-frameworks-index', 'content-curriculum-frameworks-browse'],
            isVisible: true,
            // Names the same capability the pages require, so the padlock and the page agree.
            capability: Capability.AdminContentView,
          },
          {
            to: '/content/preview-tokens',
            label: 'Preview Tokens',
            iconPath: ICONS.eye,
            activeRoutes: ['content-preview-token-list-index'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
        ],
      },
      {
        label: 'Campaigns',
        items: [
          {
            to: '/content/campaigns',
            label: 'Campaigns',
            iconPath: ICONS.marketingCampaign,
            activeRoutes: ['content-campaign-list-index'],
            isVisible: true,
            capability: Capability.AdminCampaignsView,
          },
          {
            to: '/content/partners',
            label: 'Partners',
            iconPath: ICONS.addTeam,
            activeRoutes: ['content-partners-index'],
            isVisible: true,
            capability: Capability.AdminCampaignsView,
          },
          {
            to: '/content/expos',
            label: t('content.expos.title'),
            iconPath: ICONS.calendar,
            activeRoutes: ['content-expos-index'],
            isVisible: true,
            capability: Capability.AdminCampaignsView,
          },
        ],
      },
      {
        label: t('content.institutionPromotions.navGroup'),
        items: [
          {
            to: '/content/institution-promotions',
            label: t('content.institutionPromotions.nav'),
            iconPath: ICONS.calendar,
            activeRoutes: ['content-institution-promotions-index'],
            isVisible: true,
            capability: Capability.AdminContentView,
          },
        ],
      },
      {
        label: USERS_NAV_GROUP_LABEL,
        items: [
          {
            to: '/content/users',
            label: t('content.users.students.title'),
            iconPath: ICONS.userCircle,
            activeRoutes: ['content-users-index'],
            isVisible: true,
            capability: Capability.AdminUsersView,
            // A user record is reachable from either cohort list, so this item claims the list
            // itself and nothing below it — the detail page names the group in its own crumbs.
            claimsDescendants: false,
          },
          {
            to: '/content/users/teachers',
            label: t('content.users.teachers.title'),
            iconPath: ICONS.school,
            activeRoutes: ['content-users-teachers'],
            isVisible: true,
            capability: Capability.AdminUsersView,
          },
          {
            to: '/content/users/login-attempts',
            label: t('content.loginAttempts.nav'),
            iconPath: ICONS.textSearch,
            activeRoutes: ['content-users-login-attempts'],
            isVisible: true,
            capability: Capability.AdminUsersView,
          },
        ],
      },
      {
        label: 'Rewards',
        items: [
          {
            to: isUsa ? '/content/referrals/redemptions' : '/content/referrals',
            label: t('content.referrals.nav'),
            iconPath: ICONS.userLove,
            activeRoutes: isUsa ? ['content-referrals-redemptions'] : ['content-referrals'],
            isVisible: isAus || isUsa,
            capability: Capability.AdminRewardsView,
          },
          {
            to: '/content/cash-outs',
            label: t('content.cashOuts.nav'),
            iconPath: ICONS.dollarSquare,
            activeRoutes: ['content-cash-outs'],
            isVisible: (isAus || isUsa) && cashoutAdminEnabled,
            capability: Capability.AdminRewardsView,
          },
          {
            to: '/content/redemption-batches',
            label: t('content.redemptionBatches.nav'),
            iconPath: ICONS.listSolid,
            activeRoutes: ['content-redemption-batches', 'content-redemption-batch-detail'],
            isVisible: isAus && cashoutAdminEnabled,
            capability: Capability.AdminRewardsView,
          },
          {
            to: '/content/award-coins',
            label: t('content.awardCoins.nav'),
            iconPath: ICONS.coinsDollar,
            activeRoutes: ['content-award-coins'],
            isVisible: true,
            // The page is only an action, so it answers to the edit capability the award needs.
            capability: Capability.AdminRewardsEdit,
          },
          {
            to: '/content/award-coins/history',
            label: t('content.awardCoins.history.nav'),
            iconPath: ICONS.listSolid,
            activeRoutes: ['content-award-coins-history'],
            isVisible: true,
            capability: Capability.AdminRewardsEdit,
          },
        ],
      },
      {
        label: 'Legal',
        items: [
          {
            to: '/content/terms',
            label: t('content.terms.title'),
            iconPath: ICONS.judge,
            activeRoutes: ['content-terms-index', 'content-terms-edit'],
            isVisible: true,
            capability: Capability.AdminLegalView,
          },
        ],
      },
      {
        label: 'Schools',
        items: [
          {
            to: '/content/schools/connections',
            label: t('content.schoolsConnections.nav'),
            iconPath: ICONS.school,
            activeRoutes: ['content-schools-connections-index', 'content-schools-connections-detail', 'content-schools-connections-run'],
            isVisible: true,
            capability: Capability.AdminSchoolsView,
          },
          {
            to: '/content/schools/refused-sign-up-domains',
            label: t('content.schoolsConnections.refusedDomains.nav'),
            iconPath: ICONS.filterMail,
            activeRoutes: ['content-schools-refused-sign-up-domains'],
            isVisible: true,
            capability: Capability.AdminSchoolsView,
          },
        ],
      },
      {
        label: 'Coach',
        items: [
          {
            to: '/content/conversation-review',
            label: t('general.nav.conversationReview'),
            iconPath: ICONS.search,
            activeRoutes: ['content-conversation-review-index', 'content-conversation-review-user'],
            isVisible: true,
            capability: Capability.AdminCoachView,
          },
        ],
      },
      {
        label: 'Admins',
        items: [
          {
            to: '/content/admins',
            label: t('content.admins.nav'),
            iconPath: ICONS.shield02,
            activeRoutes: ['content-admins-index'],
            isVisible: true,
            capability: Capability.AdminAdminsView,
          },
        ],
      },
    ];

    return {
      topItems: buildItems([
        {
          to: '/content/dashboard',
          label: 'Dashboard',
          iconPath: ICONS.dashboardSquare,
          activeRoutes: ['content-dashboard'],
          isVisible: true,
        },
      ]),
      groups: groups
        .map<AdminNavGroup>(g => ({
          ...g,
          subGroups: g.subGroups
            ?.map<AdminNavSubGroup>(s => ({ ...s, items: buildItems(s.items) }))
            .filter(s => s.items.length > 0),
          items: g.items ? buildItems(g.items) : undefined,
        }))
        .filter(g => (g.items?.length ?? 0) > 0 || (g.subGroups?.length ?? 0) > 0),
      footerItems: buildItems([
        {
          label: t('general.nav.profile'),
          iconPath: ICONS.userCircle,
          isVisible: passwordLoginEnabled,
          onClick: async () => {
            await navigateTo('/content/account');
          },
        },
        {
          label: t('general.nav.logout'),
          iconPath: ICONS.logout,
          isVisible: true,
          onClick: logout,
        },
      ]),
    };
  });

  return { nav };
};
